import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Building2, ChevronDown, Copy, Pencil, Printer, RefreshCcw, Recycle, Trash2, UserRound } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError, api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { AssetLink } from '../../components/assets/AssetLink';
import { Badge, Button, ButtonLink, EmptyState, ErrorBox, Loading, Notice, PageHeader, StatusBadge, Tabs } from '../../components/ui';
import { formatDate } from '../../lib/format';
import { AssignDialog, DeleteAssetDialog, LocationDialog, StatusDialog } from './LifecycleDialogs';
import { AssignmentsTab } from './tabs/AssignmentsTab';
import { ComponentsTab } from './tabs/ComponentsTab';
import { FilesTab } from './tabs/FilesTab';
import { HistoryTab } from './tabs/HistoryTab';
import { OverviewTab } from './tabs/OverviewTab';
import { TaxTab } from './tabs/TaxTab';

type TabKey = 'overview' | 'components' | 'files' | 'history' | 'periods' | 'tax';
type DialogKey = 'assign' | 'location' | 'status' | 'delete' | null;

export function AssetDetailPage() {
  const { ref = '' } = useParams();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as TabKey) || 'overview';
  const [dialog, setDialog] = useState<DialogKey>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  const q = useQuery({
    queryKey: ['asset', ref],
    queryFn: () => unwrap(api.GET('/api/assets/{ref}', { params: { path: { ref } } })),
  });

  if (q.isPending) return <Loading />;
  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 404)
      return (
        <EmptyState title={`Asset „${ref}“ nicht gefunden`}>
          <ButtonLink to="/assets" className="mt-2">
            Zur Liste
          </ButtonLink>
        </EmptyState>
      );
    return <ErrorBox error={q.error} />;
  }

  const a = q.data;
  const disposed = a.status === 'disposed';
  const deleted = !!a.deleted_at;
  const readOnly = disposed || deleted;
  const installed = !!a.parent;
  const canWrite = can('asset:write') && !readOnly;

  return (
    <>
      <PageHeader
        back={
          <Link to="/assets" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Assets
          </Link>
        }
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono">{a.asset_number}</span>
            <span className="font-normal text-slate-400">·</span>
            <span>{a.name}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={a.status} />
            <Badge>{a.asset_type.label}</Badge>
            {a.tracking_type === 'component' && <Badge tone="violet">Komponente</Badge>}
            {deleted && <Badge tone="red">Gelöscht (Fehlerfassung)</Badge>}
            {[a.manufacturer, a.model].filter(Boolean).join(' ')}
          </span>
        }
        actions={
          <>
            <Button icon={<Printer className="size-4" />} onClick={() => window.open(`/labels?assets=${a.asset_number}`, '_blank')}>
              Etikett
            </Button>
            {can('asset:write') && (
              <ButtonLink to={`/assets/new?copy=${encodeURIComponent(a.asset_number)}`} icon={<Copy className="size-4" />} title="Neues Asset mit denselben Daten anlegen">
                Kopieren
              </ButtonLink>
            )}
            {canWrite && (
              <>
                <ButtonLink to={`/assets/${a.asset_number}/edit`} icon={<Pencil className="size-4" />}>
                  Bearbeiten
                </ButtonLink>
                {!installed && (
                  <>
                    <Button icon={<UserRound className="size-4" />} onClick={() => setDialog('assign')}>
                      Zuordnen
                    </Button>
                    <Button icon={<Building2 className="size-4" />} onClick={() => setDialog('location')}>
                      Standort
                    </Button>
                  </>
                )}
                <div className="relative">
                  <Button icon={<ChevronDown className="size-4" />} onClick={() => setMoreOpen((o) => !o)} aria-expanded={moreOpen}>
                    Mehr
                  </Button>
                  {moreOpen && (
                    <div className="absolute right-0 z-20 mt-1 w-56 rounded-md border border-slate-200 bg-white py-1 shadow-lg" onMouseLeave={() => setMoreOpen(false)}>
                      <MenuItem icon={<RefreshCcw className="size-4" />} onClick={() => (setDialog('status'), setMoreOpen(false))}>
                        Status ändern
                      </MenuItem>
                      {can('asset:dispose') && (
                        <MenuLink to={`/assets/${a.asset_number}/dispose`} icon={<Recycle className="size-4" />}>
                          Verschrotten …
                        </MenuLink>
                      )}
                      {can('asset:delete') && (
                        <MenuItem icon={<Trash2 className="size-4" />} danger onClick={() => (setDialog('delete'), setMoreOpen(false))}>
                          Als Fehlerfassung löschen
                        </MenuItem>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        }
      />

      {disposed && a.disposal && (
        <Notice tone="warning" className="mb-4">
          Verschrottet am {formatDate(a.disposal.disposal_date)} –{' '}
          <Link className="font-medium underline" to={`/disposals/${a.disposal.disposal_number}`}>
            Vorgang {a.disposal.disposal_number}
          </Link>
          . Das Asset kann nicht mehr geändert werden.
        </Notice>
      )}
      {deleted && (
        <Notice tone="warning" className="mb-4">
          Als Fehlerfassung gelöscht am {formatDate(a.deleted_at)}: {a.delete_reason}
        </Notice>
      )}
      {installed && !readOnly && (
        <Notice className="mb-4">
          Eingebaut in <AssetLink asset={a.parent} /> {a.parent?.name}. Mitarbeiter und Standort werden vom Gerät übernommen.
        </Notice>
      )}

      <Tabs<TabKey>
        active={tab}
        onChange={(k) => setParams(k === 'overview' ? {} : { tab: k }, { replace: true })}
        tabs={[
          { key: 'overview', label: 'Übersicht' },
          { key: 'components', label: 'Komponenten', count: a.children_count },
          { key: 'files', label: 'Dateien', count: a.document_count + a.photo_count },
          { key: 'history', label: 'Historie' },
          { key: 'periods', label: 'Zeiträume' },
          { key: 'tax', label: 'Steuerwerte' },
        ]}
      />

      {tab === 'overview' && <OverviewTab asset={a} canWrite={canWrite} />}
      {tab === 'components' && <ComponentsTab asset={a} canWrite={canWrite} />}
      {tab === 'files' && <FilesTab asset={a} />}
      {tab === 'history' && <HistoryTab asset={a} />}
      {tab === 'periods' && <AssignmentsTab asset={a} />}
      {tab === 'tax' && <TaxTab asset={a} canWrite={can('asset:write') && !deleted} canDelete={can('asset:delete') && !deleted} />}

      <AssignDialog asset={a} open={dialog === 'assign'} onClose={() => setDialog(null)} />
      <LocationDialog asset={a} open={dialog === 'location'} onClose={() => setDialog(null)} />
      <StatusDialog asset={a} open={dialog === 'status'} onClose={() => setDialog(null)} />
      <DeleteAssetDialog asset={a} open={dialog === 'delete'} onClose={() => setDialog(null)} />
    </>
  );
}

function MenuItem({ children, icon, onClick, danger }: { children: React.ReactNode; icon: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button type="button" onClick={onClick} className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50 ${danger ? 'text-red-600' : 'text-slate-700'}`}>
      {icon}
      {children}
    </button>
  );
}

function MenuLink({ children, icon, to }: { children: React.ReactNode; icon: React.ReactNode; to: string }) {
  return (
    <Link to={to} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
      {icon}
      {children}
    </Link>
  );
}
